// Imports
import React, { useEffect, useState } from 'react';
import { Params, useParams } from 'react-router-dom';
import styled from 'styled-components/macro';
import { useAppDispatch } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import theme from '../../../../../../shareables/theme';
import { GetPaymentResponse } from '../../../../../../shareables/types/api/main/website/payment/get';
import formatCurrency from '../../../../../../shareables/utils/project-specific/formatting/format-currency';
import Table from '../../page-types/shared-components/table';
import PageTitle from '../../shared-components/page-title';


// Styled components
const Container = styled.div`
	margin: auto;
	padding: 1.875rem;
	background: #fff;
	border: 0.0625rem solid #eee;
	box-shadow: 0 0 0.625rem rgb(0, 0, 0, 0.15);
	font-size: 1rem;
	line-height: 1.5rem;
	font-family: 'Helvetica Neue', 'Helvetica', Helvetica, Arial, sans-serif;
	color: #555;
	
	table:not(:last-of-type) {
		margin-bottom: 1.5rem;
	}
`;

const TopPart = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-template-rows: 1fr 1fr;
	align-items: center;
	row-gap: 1.25rem;
	margin-bottom: 1.5rem;
	padding: 0.625rem;
	
	@media (min-width: ${theme.breakpoints.mobile + 1}px) {
		> div:nth-child(even) {
			text-align: right;
		}
	}
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		grid-template-columns: 1fr;
		grid-auto-flow: row;
		row-gap: 0.75rem;
	}
`;

const Title = styled.div`
	font-size: 2.8125rem;
	line-height: 2.8125rem;
	color: #333;
	
	@media (min-width: ${theme.breakpoints.mobile}px) {
		margin-bottom: 0.6875rem;
	}
`;

const Paid = styled.div`
	@media (min-width: ${theme.breakpoints.mobile}px) {
		margin-bottom: 0.6875rem;
	}
`;


// Define the accepted props
interface Props {
	storybookPayment?: GetPaymentResponse;
}


// Function component
const Receipt: React.FC<Props> = ({ storybookPayment }) => {
	// Use state
	const [payment, setPayment] = useState<GetPaymentResponse | null>(storybookPayment ?? null);
	
	
	// Use React Router functionality
	const { paymentID } = useParams<Params>();
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Load payment on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetPaymentResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/payment/${paymentID ?? ''}`,
				completion: (json) => {
					setPayment(json.response);
				},
			})
		);
	}, [dispatch, paymentID]);
	
	
	// If loading, then display nothing
	if (payment === null) {
		return null;
	}
	
	
	// Return JSX
	return (
		<React.Fragment>
			<PageTitle>Online payment receipt</PageTitle>
			
			<Container>
				<TopPart>
					<Title>Receipt</Title>
					
					<Paid>
						<span className='is-gray'>Paid:</span>{' '}
						{new Date(payment.payment.received).toLocaleDateString(undefined, {
							month: 'long',
							day: 'numeric',
							year: 'numeric',
							hour: 'numeric',
							minute: '2-digit',
						})}
					</Paid>
					
					<div>
						<div>{payment.community.name}</div>
						<div>{payment.community.location}</div>
					</div>
					
					<div>
						<div>{payment.payer?.name ?? <em>Member no longer exists</em>}</div>
						<div>{payment.payer?.email}</div>
					</div>
				</TopPart>
				
				<Table>
					<thead>
						<tr>
							<th style={{ textAlign: 'left' }}>Payment method</th>
							<th style={{ textAlign: 'right' }}>Currency</th>
						</tr>
					</thead>
					
					<tbody>
						<tr>
							<td>{payment.payment.paymentMethodDescriptor}</td>
							<td style={{ textAlign: 'right' }}>{payment.payment.currencyCode}</td>
						</tr>
					</tbody>
				</Table>
				
				<Table>
					<thead>
						<tr>
							<th style={{ textAlign: 'left' }}>Item</th>
							<th style={{ textAlign: 'right' }}>Amount</th>
						</tr>
					</thead>
					
					<tbody>
						<tr>
							<td>{payment.paymentOption.title}</td>
							<td style={{ textAlign: 'right' }}>{formatCurrency(payment.payment.amount)}</td>
						</tr>
						
						<tr>
							<td>Transaction fee</td>
							<td style={{ textAlign: 'right' }}>{formatCurrency(payment.payment.fee)}</td>
						</tr>
						
						<tr>
							<td style={{ textAlign: 'right', width: '100%' }}>
								<strong>Total</strong>
							</td>
							
							<td style={{ textAlign: 'right', minWidth: '6.5rem' }}>
								<strong>{formatCurrency(payment.payment.total)}</strong>
							</td>
						</tr>
					</tbody>
				</Table>
			</Container>
		</React.Fragment>
	);
};

export default Receipt;
